// @/src/core/services/configuracion.service.ts

import { db } from '@/core/config/firebase';
import { 
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp,
    type DocumentReference,
    type UpdateData
} from 'firebase/firestore';
import { COLECCIONES, MAIN_DATA_DOC_ID } from '@/core/constants/collections';
import { getErrorCode, getErrorMessage } from '@shared/utils/errores';
import type { 
    MainData, 
    ProductoConfig,
    IngresoInventario,
    Categoria,
    MetodoPago,
    EmpresaMensajeria,
    MetodoIngreso
} from '@/core/types';
import { 
    DEFAULT_CATEGORIAS_EGRESOS_NEGOCIO,
    DEFAULT_METODOS_PAGO_EGRESOS_NEGOCIO,
    DEFAULT_CATEGORIAS_GASTOS_PERSONALES,
    DEFAULT_METODOS_PAGO_GASTOS_PERSONALES,
    DEFAULT_METODOS_INGRESO_OTROS,
    DEFAULT_EMPRESAS_MENSAJERIA
} from '@/core/constants/defaults';

// Importar el store de inventario para enviar señales
import { publicInventarioStore } from '@/core/store/inventario.store';
// Importar el store de configuración para recargarlo después de cambios
import { configuracionStore } from '@/core/store/configuracion.store';

/**
 * Obtiene la referencia al documento 'mainData' de un usuario.
 * @param userId - El ID del usuario.
 * @returns La referencia al documento de Firestore.
 */
const getMainDataRef = (userId: string): DocumentReference<MainData> => {
    return doc(db, COLECCIONES.USUARIOS, userId, COLECCIONES.APP_DATA, MAIN_DATA_DOC_ID) as DocumentReference<MainData>;
};

/**
 * Verifica si el documento de configuración existe y lo inicializa con valores
 * por defecto si es necesario. Es una operación de una sola lectura, optimizada en costos.
 * @param userId - El ID del usuario.
 * @returns Una promesa que se resuelve cuando la verificación y posible inicialización terminan.
 */
export const initializeConfigurationDataIfNeeded = async (userId: string): Promise<void> => {
    if (!userId) {
        throw new Error('[ConfigService] Se requiere un ID de usuario para inicializar datos.');
    }
    
    console.log(`[ConfigService] 🔍 Verificando documento de configuración para usuario: ${userId}`);
    const mainDataRef = getMainDataRef(userId);
    const docSnap = await getDoc(mainDataRef);

    if (docSnap.exists()) {
        console.log(`[ConfigService] ✅ Documento existe. Verificando campos faltantes...`);
        // El documento existe, pero verificamos si faltan arrays de datos esenciales
        const data = docSnap.data();
        const updates: UpdateData<MainData> = {};
        const now = new Date();
        
        if (!data.customCategoriasEgresosData) {
            updates.customCategoriasEgresosData = DEFAULT_CATEGORIAS_EGRESOS_NEGOCIO.map(cat => ({...cat, timestamp: now}));
        }
        if (!data.customMetodosPagoEgresosData) {
            updates.customMetodosPagoEgresosData = DEFAULT_METODOS_PAGO_EGRESOS_NEGOCIO.map(met => ({...met, timestamp: now}));
        }
        if (!data.customCategoriasGastosPersonalesData) {
            updates.customCategoriasGastosPersonalesData = DEFAULT_CATEGORIAS_GASTOS_PERSONALES.map(cat => ({...cat, timestamp: now}));
        }
        if (!data.customMetodosPagoGastosPersonalesData) {
            updates.customMetodosPagoGastosPersonalesData = DEFAULT_METODOS_PAGO_GASTOS_PERSONALES.map(met => ({...met, timestamp: now}));
        }
        if (!data.metodosIngresoOtrosData) {
            updates.metodosIngresoOtrosData = DEFAULT_METODOS_INGRESO_OTROS.map(met => ({...met, timestamp: now}));
        }
        if (!data.empresasMensajeriaData || data.empresasMensajeriaData.length === 0) {
            updates.empresasMensajeriaData = DEFAULT_EMPRESAS_MENSAJERIA.map(emp => ({...emp, timestamp: now}));
        }

        if (Object.keys(updates).length > 0) {
            console.log(`[ConfigService] 🔧 Inicializando ${Object.keys(updates).length} campos faltantes...`);
            await updateDoc(mainDataRef, updates);
            console.log(`[ConfigService] ✅ Campos faltantes inicializados exitosamente.`);
        } else {
            console.log(`[ConfigService] ✅ Documento completo, no se necesitan actualizaciones.`);
        }

    } else {
        // El documento no existe, lo creamos con todos los valores por defecto.
        console.log(`[ConfigService] ⚠️ El documento 'mainData' no existe para ${userId}. Creando con valores por defecto.`);
        const now = new Date();
        const initialData: MainData = {
            configProductosData: [],
            ingresosInventarioData: [],
            empresasMensajeriaData: DEFAULT_EMPRESAS_MENSAJERIA.map(emp => ({...emp, timestamp: now})),
            ventasData: [],
            egresosNegocioData: [],
            gastosPersonalesData: [],
            customCategoriasEgresosData: DEFAULT_CATEGORIAS_EGRESOS_NEGOCIO.map(cat => ({...cat, timestamp: now})),
            customMetodosPagoEgresosData: DEFAULT_METODOS_PAGO_EGRESOS_NEGOCIO.map(met => ({...met, timestamp: now})),
            customCategoriasGastosPersonalesData: DEFAULT_CATEGORIAS_GASTOS_PERSONALES.map(cat => ({...cat, timestamp: now})),
            customMetodosPagoGastosPersonalesData: DEFAULT_METODOS_PAGO_GASTOS_PERSONALES.map(met => ({...met, timestamp: now})),
            metodosIngresoOtrosData: DEFAULT_METODOS_INGRESO_OTROS.map(met => ({...met, timestamp: now})),
            timestampCreacionDoc: serverTimestamp()
        };
        await setDoc(mainDataRef, initialData);
        console.log(`[ConfigService] ✅ Documento 'mainData' creado exitosamente para ${userId}.`);
    }
};

/**
 * Obtiene los datos de configuración una sola vez desde Firestore.
 * @param userId - El ID del usuario.
 * @returns Los datos del documento 'mainData' o null si no existe.
 */
export const getConfigurationData = async (userId: string): Promise<MainData | null> => {
    if (!userId) {
        console.error('[ConfigService] No se puede obtener la configuración sin ID de usuario.');
        return null;
    }
    const mainDataRef = getMainDataRef(userId);
    const docSnap = await getDoc(mainDataRef);
    return docSnap.exists() ? docSnap.data() : null;
};

/** Claves de MainData cuyo valor es un array (las únicas válidas para updateConfigurationArray). */
type ArrayKeysOfMainData = { [K in keyof MainData]: MainData[K] extends unknown[] ? K : never }[keyof MainData];

/**
 * Sobrescribe el array completo de un tipo de configuración específico.
 * Es ideal para operaciones de borrado, reordenamiento o adición.
 * @param userId - El ID del usuario.
 * @param field - La clave del campo a actualizar en el documento MainData.
 * @param newArray - El nuevo array completo que reemplazará al anterior.
 */
const updateConfigurationArray = async <K extends ArrayKeysOfMainData>(
    userId: string,
    field: K,
    newArray: MainData[K]
): Promise<void> => {
    console.log(`[ConfigService] 🔄 Iniciando actualización de '${field}' para usuario: ${userId}`);
    console.log(`[ConfigService] 📊 Tamaño del array: ${newArray.length} elementos`);
    
    if (!userId) {
        const error = `[ConfigService] Se requiere ID de usuario para actualizar ${field}.`;
        console.error(error);
        throw new Error(error);
    }
    
    const mainDataRef = getMainDataRef(userId);
    console.log(`[ConfigService] 📍 Referencia del documento:`, mainDataRef.path);
    
    try {
        console.log(`[ConfigService] 🚀 Ejecutando updateDoc para '${field}'...`);
        await updateDoc(mainDataRef, { [field]: newArray });
        console.log(`[ConfigService] ✅ Array '${field}' actualizado exitosamente para el usuario ${userId}.`);

        // 🔄 SEÑAL: Emitir señal si el cambio afecta al inventario
        if (field === 'configProductosData') {
            console.log(`[ConfigService] 🔄 Marcando inventario para actualización debido a cambio en productos`);
            publicInventarioStore.markForUpdate();
        } else if (field === 'ingresosInventarioData') {
            console.log(`[ConfigService] 🔄 Marcando inventario para actualización debido a cambio en ingresos`);
            publicInventarioStore.markForUpdate();
        }

        // 🔄 RECARGA: Recargar store de configuración para tener datos actualizados
        console.log(`[ConfigService] 🔄 Recargando store de configuración después de actualizar '${field}'`);
        await configuracionStore.reload();

    } catch (error) {
        console.error(`[ConfigService] ❌ Error actualizando el array '${field}':`, error);
        console.error(`[ConfigService] 🔍 Detalles del error:`, {
            code: getErrorCode(error),
            message: getErrorMessage(error),
            userId,
            field,
            arrayLength: newArray.length
        });
        throw new Error(`No se pudo guardar la lista de ${field}.`);
    }
};

// --- Funciones de servicio específicas y tipadas ---

export const updateConfigProductos = (userId: string, productos: ProductoConfig[]) =>
    updateConfigurationArray(userId, 'configProductosData', productos);

export const updateIngresosInventario = (userId: string, ingresos: IngresoInventario[]) =>
    updateConfigurationArray(userId, 'ingresosInventarioData', ingresos);

export const updateCategoriasEgresos = (userId: string, categorias: Categoria[]) =>
    updateConfigurationArray(userId, 'customCategoriasEgresosData', categorias);

export const updateMetodosPagoEgresos = (userId: string, metodos: MetodoPago[]) =>
    updateConfigurationArray(userId, 'customMetodosPagoEgresosData', metodos);

export const updateCategoriasGastosPersonales = (userId: string, categorias: Categoria[]) =>
    updateConfigurationArray(userId, 'customCategoriasGastosPersonalesData', categorias);

export const updateMetodosPagoGastosPersonales = (userId: string, metodos: MetodoPago[]) =>
    updateConfigurationArray(userId, 'customMetodosPagoGastosPersonalesData', metodos);

export const updateEmpresasMensajeria = (userId: string, empresas: EmpresaMensajeria[]) =>
    updateConfigurationArray(userId, 'empresasMensajeriaData', empresas);

export const updateMetodosIngreso = (userId: string, metodos: MetodoIngreso[]) =>
    updateConfigurationArray(userId, 'metodosIngresoOtrosData', metodos);

export const setEmpresaMensajeriaPrincipal = async (
    userId: string,
    empresaId: string
): Promise<void> => {
    const configData = await getConfigurationData(userId);
    let list = configData?.empresasMensajeriaData || [];

    if (list.length === 0) {
        const now = new Date();
        list = DEFAULT_EMPRESAS_MENSAJERIA.map((emp) => ({
            ...emp,
            timestamp: now,
        })) as EmpresaMensajeria[];
    }

    const updatedList = list.map((emp) => ({
        ...emp,
        isPrincipal: emp.id === empresaId || emp.codigoEmpresa === empresaId,
    }));

    await updateEmpresasMensajeria(userId, updatedList);
};